/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {SelectionModel} from "@angular/cdk/collections";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit
} from "@angular/core";
import {MatTableDataSource} from "@angular/material/table";
import {ConfigSectionComponent} from "@app/models/components/config-section.component";
import {RegistryContexts} from "@app/models/constants/registry-constants";
import {CommonState} from "@app/store/common/common.state";
import {Goal} from "@modules/doctor-panel/modules/patient-treatment-plan/models/goal";
import {PatientTreatmentPlanAdvState} from "@modules/doctor-panel/modules/patient-treatment-plan/store/patient-treatment-plan-adv.state";
import {hasActionsExecuting} from "@ngxs-labs/actions-executing";
import {Select, Store} from "@ngxs/store";
import {UserEntry2} from "@trec/api/registry";
import {StoreAction} from "@trec/components/store";
import {BehaviorSubject, combineLatest, Observable, Subscription} from "rxjs";
import {TpAdvActions} from "../../../store/patient-treatment-plan-adv.actions";

@Component({
  selector: "c3-config-goals",
  templateUrl: "./config-goals.component.html",
  styleUrls: ["./config-goals.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ConfigGoalsComponent
  extends ConfigSectionComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @Select(PatientTreatmentPlanAdvState.availableGoals)
  availableGoals$: Observable<Goal[] | undefined>;

  @Select(CommonState.patientUserEntry)
  patientUserEntry$: Observable<UserEntry2[] | null | undefined>;

  @Select(hasActionsExecuting([TpAdvActions.FetchGoals, TpAdvActions.UpdateGoals]))
  loading$: Observable<boolean>;

  @Input()
  context: RegistryContexts[];

  private valid = new BehaviorSubject(false);
  private touched = new BehaviorSubject(false);

  get touched$(): Observable<boolean> {
    return this.touched.asObservable();
  }

  get valid$(): Observable<boolean> {
    return this.valid.asObservable();
  }

  readonly today = new Date();

  /**
   * List of columns to display in the table.
   */
  displayedColumns: string[] = ["select", "name", "startDate"];

  // Private fields ///////////////////////////////////////////////////////////////////////////////

  dataSource = new MatTableDataSource<Goal>();

  // Selection //////////////////////////////////
  readonly initialSelection: Goal[] = [];
  selection = new SelectionModel<Goal>(false, this.initialSelection);

  private subscr: Subscription;

  constructor(private store: Store, private cd: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    this.store.dispatch(new TpAdvActions.FetchGoals());
  }

  ngAfterViewInit() {
    this.subscr = combineLatest([this.availableGoals$, this.patientUserEntry$]).subscribe(
      ([availableList, ue]) => {
        if (availableList && ue) {
          const ueKeysGoal = ue.filter(x => x.getKeys("GOAL"));

          let list: Goal[] = [];
          let newSelection: Goal[] = [];
          ueKeysGoal.forEach(ueKeys => {
            const goalKeys = ueKeys.getKeys("GOAL");
            // Copy available goals (objects from store are freezed)
            list = [...availableList
              .map((g) => ({...g}))
              .map((g) => {
                // Find goal in keys
                const goalKey = goalKeys.find((key) => key.name === g.id);
                return goalKey && goalKey.start_date
                  ? {...g, startDate: new Date(goalKey.start_date)}
                  : g;
              })];
            // Select current active goals
            const selection = list.filter((g) => {
              const goalKey = goalKeys.find((key) => key.name === g.id);
              return goalKey;
            });

            // Select current active goals
            newSelection = newSelection?.concat(selection);
          });

          this.dataSource.data = list;
          this.selection = new SelectionModel(true, newSelection);

          this.valid.next(true);

          this.cd.markForCheck();
        }
      }
    );
  }

  /**
   * Whether the number of selected elements matches the total number of rows.
   */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /**
   * Selects all rows if they are not all selected; otherwise clear selection.
   */
  masterToggle() {
    this.touched.next(true);

    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach((row) => this.selection.select(row));
  }

  onSelectionChange(goal: Goal) {
    this.touched.next(true);
    this.selection.toggle(goal);

    // Set current date
    goal.startDate = this.today;
  }

  onStartDateChange() {
    this.touched.next(true);
  }

  prepareSaveDataAction(): StoreAction | null {
    // TODO validate

    // Create the selection array
    const selectedGoals: Goal[] = this.selection.selected;
    // Trigger update
    return new TpAdvActions.UpdateGoals({selectedGoals});
  }

  resetTouched(): void {
    this.touched.next(false);
  }

  ngOnDestroy() {
    if (this.subscr) {
      this.subscr.unsubscribe();
    }

    this.valid.complete();
    this.touched.complete();
  }
}
